/**
 * Nara orb — personalities (moods) + voice phases.
 * Source: Nara-App personality/naraPersonality.js (DEFAULT stays site gold #D39542).
 */
export const NARA_ORB_GOLD = '#D39542';

export const NARA_ORB_PHASES = Object.freeze([
  'idle',
  'listening',
  'thinking',
  'processing',
  'speaking',
]);

export const NARA_PERSONALITY_IDS = Object.freeze([
  'DEFAULT',
  'STRICT',
  'ENERGY',
  'CALM',
  'HEALTH',
  'MINDSET',
  'SUCCESS',
  'MEDITATION',
]);

export const NARA_HOME_CYCLE_MOODS = Object.freeze([
  'CALM',
  'ENERGY',
  'SUCCESS',
  'STRICT',
  'MINDSET',
]);

export const NARA_PERSONALITIES = Object.freeze({
  DEFAULT: {
    id: 'DEFAULT',
    mood: 'mood_default',
    label: 'Default',
    color: '#D39542',
    secondaryColor: '#F0C56A',
    glowColor: '#E2C17E',
    glowIntensity: 1,
    haloStrength: 1,
    breathAmplitude: 1,
    breathFrequency: 1,
    rotationSpeed: 1,
    turbulence: 1,
    noiseStrength: 1,
    fluidity: 1,
    particleSpeed: 1,
    particleBrightness: 1,
    particleSize: 1,
    particleDensity: 1,
    audioReactivity: 1,
    transitionDuration: 1,
    movementType: 'orbit',
    globalEnergy: 0.55,
    voiceTone: 'warm',
  },
  STRICT: {
    id: 'STRICT',
    mood: 'mood_strict',
    label: 'Strict',
    color: '#8B2E2E',
    secondaryColor: '#C45C5C',
    glowColor: '#A33B3B',
    glowIntensity: 0.88,
    haloStrength: 0.9,
    breathAmplitude: 0.82,
    breathFrequency: 1.08,
    rotationSpeed: 0.92,
    turbulence: 0.78,
    noiseStrength: 0.86,
    fluidity: 0.84,
    particleSpeed: 0.94,
    particleBrightness: 0.92,
    particleSize: 0.96,
    particleDensity: 1.02,
    audioReactivity: 0.9,
    transitionDuration: 0.92,
    movementType: 'hold',
    globalEnergy: 0.62,
    voiceTone: 'firm',
  },
  ENERGY: {
    id: 'ENERGY',
    mood: 'mood_energy',
    label: 'Energy',
    color: '#E07A2F',
    secondaryColor: '#F0A35A',
    glowColor: '#FFB45A',
    glowIntensity: 1.18,
    haloStrength: 1.16,
    breathAmplitude: 1.22,
    breathFrequency: 1.28,
    rotationSpeed: 1.24,
    turbulence: 1.12,
    noiseStrength: 1.08,
    fluidity: 1.12,
    particleSpeed: 1.18,
    particleBrightness: 1.12,
    particleSize: 1.04,
    particleDensity: 0.96,
    audioReactivity: 1.18,
    transitionDuration: 0.88,
    movementType: 'surge',
    globalEnergy: 0.88,
    voiceTone: 'energetic',
  },
  CALM: {
    id: 'CALM',
    mood: 'mood_calm',
    label: 'Calm',
    color: '#8EC4D4',
    secondaryColor: '#C5E4EC',
    glowColor: '#A9D7E2',
    glowIntensity: 0.78,
    haloStrength: 0.86,
    breathAmplitude: 0.72,
    breathFrequency: 0.68,
    rotationSpeed: 0.62,
    turbulence: 0.7,
    noiseStrength: 0.74,
    fluidity: 1.08,
    particleSpeed: 0.72,
    particleBrightness: 0.86,
    particleSize: 1.02,
    particleDensity: 1.04,
    audioReactivity: 0.72,
    transitionDuration: 1.18,
    movementType: 'drift',
    globalEnergy: 0.32,
    voiceTone: 'calm',
  },
  HEALTH: {
    id: 'HEALTH',
    mood: 'mood_health',
    label: 'Health',
    color: '#4F8F5B',
    secondaryColor: '#8FBF96',
    glowColor: '#6EAE78',
    glowIntensity: 0.92,
    haloStrength: 0.94,
    breathAmplitude: 0.9,
    breathFrequency: 0.85,
    rotationSpeed: 0.8,
    turbulence: 0.82,
    noiseStrength: 0.84,
    fluidity: 1.02,
    particleSpeed: 0.86,
    particleBrightness: 0.94,
    particleSize: 1,
    particleDensity: 1,
    audioReactivity: 0.84,
    transitionDuration: 1.06,
    movementType: 'orbit',
    globalEnergy: 0.48,
    voiceTone: 'warm',
  },
  MINDSET: {
    id: 'MINDSET',
    mood: 'mood_mindset',
    label: 'Mindset',
    color: '#B07AA8',
    secondaryColor: '#D2A8CC',
    glowColor: '#C492BC',
    glowIntensity: 1.02,
    haloStrength: 1.04,
    breathAmplitude: 0.95,
    breathFrequency: 0.9,
    rotationSpeed: 0.88,
    turbulence: 0.9,
    noiseStrength: 0.92,
    fluidity: 1.04,
    particleSpeed: 0.9,
    particleBrightness: 1,
    particleSize: 1,
    particleDensity: 0.98,
    audioReactivity: 0.94,
    transitionDuration: 1.02,
    movementType: 'orbit',
    globalEnergy: 0.52,
    voiceTone: 'focused',
  },
  SUCCESS: {
    id: 'SUCCESS',
    mood: 'mood_success',
    label: 'Success',
    color: '#E8C15A',
    secondaryColor: '#F6E0A0',
    glowColor: '#F0D37A',
    glowIntensity: 1.22,
    haloStrength: 1.18,
    breathAmplitude: 1.08,
    breathFrequency: 1.05,
    rotationSpeed: 1.06,
    turbulence: 1.02,
    noiseStrength: 1,
    fluidity: 1.06,
    particleSpeed: 1.08,
    particleBrightness: 1.16,
    particleSize: 1.06,
    particleDensity: 0.94,
    audioReactivity: 1.08,
    transitionDuration: 0.9,
    movementType: 'bloom',
    globalEnergy: 0.78,
    voiceTone: 'bright',
  },
  MEDITATION: {
    id: 'MEDITATION',
    mood: 'mood_meditation',
    label: 'Meditation',
    color: '#B8A4D4',
    secondaryColor: '#E2C17E',
    glowColor: '#C9B6E0',
    glowIntensity: 0.7,
    haloStrength: 0.8,
    breathAmplitude: 0.62,
    breathFrequency: 0.52,
    rotationSpeed: 0.48,
    turbulence: 0.52,
    noiseStrength: 0.58,
    fluidity: 1.16,
    particleSpeed: 0.58,
    particleBrightness: 0.82,
    particleSize: 1.04,
    particleDensity: 1.06,
    audioReactivity: 0.6,
    transitionDuration: 1.28,
    movementType: 'drift',
    globalEnergy: 0.22,
    voiceTone: 'calm',
  },
});

export const MOOD_TO_PERSONALITY = Object.freeze({
  mood_default: 'DEFAULT',
  mood_strict: 'STRICT',
  mood_energy: 'ENERGY',
  mood_calm: 'CALM',
  mood_health: 'HEALTH',
  mood_mindset: 'MINDSET',
  mood_success: 'SUCCESS',
  mood_meditation: 'MEDITATION',
  default: 'DEFAULT',
  strict: 'STRICT',
  energy: 'ENERGY',
  calm: 'CALM',
  health: 'HEALTH',
  mindset: 'MINDSET',
  success: 'SUCCESS',
  meditation: 'MEDITATION',
});

export function resolvePersonalityId(value) {
  const key = String(value || 'DEFAULT').trim();
  if (NARA_PERSONALITIES[key.toUpperCase()]) return key.toUpperCase();
  return MOOD_TO_PERSONALITY[key.toLowerCase()] || 'DEFAULT';
}

export function getPersonality(value) {
  return NARA_PERSONALITIES[resolvePersonalityId(value)] || NARA_PERSONALITIES.DEFAULT;
}

window.NARA_ORB_MOODS = {
  NARA_ORB_GOLD,
  NARA_ORB_PHASES,
  NARA_PERSONALITY_IDS,
  NARA_HOME_CYCLE_MOODS,
  NARA_PERSONALITIES,
  MOOD_TO_PERSONALITY,
  resolvePersonalityId,
  getPersonality,
};
